import clsx from 'clsx';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Select } from '../Select/Select';
import { SortColumnConfig, SortPanelProps, SortSelectPanel } from './SortPanel.types';
import { AutoTestIdPrefix, getGenerateAutoTestId } from '../utils/testAutomation.utils';
import { Typography } from '../Typography/Typography';

import useStyles from './SortPanel.style';

const SortPanel: React.FC<SortPanelProps> = ({
  config,
  className,
  testId = 'sort-panel',
  isTestAutomationEnabled = false,
  autoTestId,
}) => {
  const c = useStyles({});

  const renderRoiColumn = (column: SortColumnConfig) => {
    const columnLabelText = column.label;
    return (
      <div
        key={column.id}
        className={column.className}
        data-testid={`non-sortable-${column.id}`}
      >
        {columnLabelText && (
          <span>{columnLabelText}:</span>
        )}
        {column.additionalElement && (
          <div className={c.additionalElement}>
            {column.additionalElement}
          </div>
        )}
      </div>
    );
  };


  return (
    <div
      className={clsx(c.root, config.className, className)}
      data-testid={testId}
    >
      <SortSelect config={config} isTestAutomationEnabled={isTestAutomationEnabled}/>
      {config.roiColumn && renderRoiColumn(config.roiColumn)}
    </div>
  );
};

const SortSelect: React.FC<SortSelectPanel & { isTestAutomationEnabled?: boolean }> = ({ config, isTestAutomationEnabled = false }) => {
  const c = useStyles({});
  const [t] = useTranslation('components');
  const generateAutoTestId = getGenerateAutoTestId(isTestAutomationEnabled);

  const options = useMemo(() => {
    return config.columns
                 .filter((col) => col.sortable)
                 .flatMap((col) => {
                   const createOption = (dir: 'asc' | 'desc') => {
                     const translationKey = `fund-list.sort-panel.${col.id}.${dir}`;
                     return {
                     id: col.id,
                     direction: dir,
                     key: `${col.id}_${dir}`,
                     label: `${col.label}${t(translationKey)}`,
                   }};

                   const items = col.sortableInBothDirections
                       ? [createOption('asc'), createOption('desc')]
                       : [createOption('asc')];

                   if (col.descendingFirstInSortPanel) items.reverse();
                   return items;
                 });
  }, [config.columns, t]);

  const currentOption = options.find(
      (opt) => opt.id === config.currentSort.field && opt.direction === config.currentSort.direction
  );

  const sortLabelKey = 'fund-list.sort-panel.sort';

  return (<div
          key={'sort-select'}
          data-testid={`non-sortable-sort`}
          className={c.sortSelectWrapper}
      >
        <Typography component="span">{t(sortLabelKey)}</Typography>
        <Select
            collapseButtonSize="medium"
            className={c.sortSelect}
            labelId={'fund-chart-sort-panel-id'}
            value={currentOption?.label || ''}
            options={options.map((opt) => opt.label)}
            onChange={(event) => {
              const selected = options.find((opt) => opt.label === event.target.value);
              if (selected) {
                config.onSort(selected.id, selected.direction);
              }
            }}
            autoTestId={generateAutoTestId?.(AutoTestIdPrefix.BUTTON, sortLabelKey)}
        />
      </div>
  );
};
export { SortPanel };
